import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    ChevronDown,
    Clock3,
    MapPin,
    Search as SearchIcon,
    ShieldCheck,
    Truck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { CategoryTile } from "@/components/catalog/CategoryTile";
import { ProductCard } from "@/components/catalog/ProductCard";
import { PromoBanner } from "@/components/catalog/PromoBanner";
import { SectionHeader } from "@/components/catalog/SectionHeader";
import {
    CategoryTileSkeleton,
    ProductCardSkeleton,
} from "@/components/catalog/Skeletons";
import { useAuth } from "@/features/auth/AuthContext";
import { useCategories, useProducts } from "@/features/catalog/hooks";
import { ROUTES } from "@/constants/routes";

/**
 * Static promo banners.
 *
 * These are marketing content, not product data — keeping them in the
 * page module is fine until we add an admin-managed promo module.
 */
const HOME_PROMOS = [
    {
        id: "promo-1",
        eyebrow: "Flash deal",
        headline: "Up to 40% off on fresh produce",
        body: "Everyday essentials at everyday-low prices.",
        cta: "Shop now",
        background: "bg-gradient-to-br from-brand via-brand-600 to-brand-700",
        tone: "light" as const,
    },
    {
        id: "promo-2",
        eyebrow: "Free delivery",
        headline: "On orders above ₹199",
        body: "Delivered to your door in 10 minutes.",
        cta: "Explore",
        background: "bg-gradient-to-br from-accentbrand via-accentbrand-500 to-accentbrand-700",
        tone: "dark" as const,
    },
    {
        id: "promo-3",
        eyebrow: "First-time offer",
        headline: "₹100 off your first order",
        body: "Use code TSGO100 at checkout.",
        cta: "Grab it",
        background: "bg-gradient-to-br from-ink via-slate-900 to-slate-800",
        tone: "light" as const,
    },
];

export default function Home() {
    const { user, isAuthenticated } = useAuth();
    const categoriesQuery = useCategories();
    const dealsQuery = useProducts({ tag: "deal", page_size: 6 });
    const bestsellersQuery = useProducts({ tag: "bestseller", page_size: 8 });
    const catalogQuery = useProducts({ page_size: 12 });

    const deals = dealsQuery.data?.items ?? [];
    const bestsellers = bestsellersQuery.data?.items ?? [];
    // A catalog without merchandising tags would otherwise render a home page
    // with zero Add-to-cart buttons — fall back to the live catalog.
    const showCatalogFallback =
        !dealsQuery.isLoading &&
        !bestsellersQuery.isLoading &&
        deals.length === 0 &&
        bestsellers.length === 0;

    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    }, []);
    const firstName = user?.full_name.split(" ")[0];

    return (
        <div data-testid="home-page" className="tsgo-page">
            {/* Delivery + greeting strip */}
            <section className="tsgo-container pt-4 sm:pt-6">
                <div className="flex flex-col gap-3 rounded-3xl border border-border/60 bg-gradient-to-r from-brand/5 via-background to-accentbrand/5 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex items-center gap-3">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand text-white shadow-brand">
                            <Truck className="h-5 w-5" />
                        </span>
                        <div>
                            <p
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                                data-testid="home-greeting"
                            >
                                {greeting}
                                {firstName ? `, ${firstName}` : ""} — delivery in ~12 min
                            </p>
                            <p className="text-sm font-semibold text-foreground sm:text-base">
                                {isAuthenticated
                                    ? "Everything you need, right at your door."
                                    : "Sign in to unlock personalised savings."}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="home-location"
                        className="group inline-flex items-center gap-2 rounded-full border border-border/70 bg-white px-3 py-2 text-left shadow-soft transition hover:border-brand/40"
                    >
                        <span className="grid h-8 w-8 place-items-center rounded-full bg-brand/10 text-brand">
                            <MapPin className="h-4 w-4" />
                        </span>
                        <span className="flex min-w-0 flex-col leading-tight">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Deliver to
                            </span>
                            <span className="truncate text-sm font-semibold text-foreground">
                                Detect my location
                            </span>
                        </span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition group-hover:text-foreground" />
                    </button>
                </div>
            </section>

            {/* Search */}
            <section className="tsgo-container mt-5 sm:mt-6">
                <Link
                    to={ROUTES.search}
                    className="relative block"
                    data-testid="home-search"
                >
                    <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        readOnly
                        placeholder='Search "milk", "bread", "chips"…'
                        className="h-12 cursor-pointer rounded-full pl-11 pr-4 text-base shadow-soft"
                        tabIndex={-1}
                    />
                </Link>
            </section>

            {/* Promo carousel */}
            <section className="tsgo-container mt-8">
                <div
                    className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3"
                    data-testid="home-promos"
                >
                    {HOME_PROMOS.map((banner) => (
                        <div
                            key={banner.id}
                            className="w-[86%] shrink-0 snap-start sm:w-auto"
                        >
                            <PromoBanner banner={banner} />
                        </div>
                    ))}
                </div>
            </section>

            {/* Categories */}
            <section className="tsgo-container mt-10">
                <SectionHeader
                    title="Shop by category"
                    subtitle="Fresh essentials, delivered fast"
                    action={{ label: "See all", to: ROUTES.categories }}
                />
                {categoriesQuery.isLoading ? (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7">
                        {Array.from({ length: 12 }).map((_, i) => (
                            <CategoryTileSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7"
                        data-testid="home-categories"
                    >
                        {(categoriesQuery.data ?? []).slice(0, 12).map((c) => (
                            <CategoryTile key={c.id} category={c} />
                        ))}
                    </div>
                )}
            </section>

            {/* Deals */}
            {showCatalogFallback ? (
                <section className="tsgo-container mt-12">
                    <SectionHeader
                        title="All products"
                        subtitle="Everything in store right now"
                        action={{ label: "See all", to: ROUTES.categories }}
                    />
                    {catalogQuery.isLoading ? (
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <ProductCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : (
                        <div
                            className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                            data-testid="home-catalog"
                        >
                            {(catalogQuery.data?.items ?? []).map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    )}
                </section>
            ) : (
                <>
            <section className="tsgo-container mt-12">
                <SectionHeader
                    title="Deals of the moment"
                    subtitle="Up to 40% off on today's picks"
                    action={{ label: "See all", to: ROUTES.categories }}
                />
                {dealsQuery.isLoading ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <ProductCardSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                        data-testid="home-deals"
                    >
                        {deals.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </section>

            {/* Bestsellers */}
            <section className="tsgo-container mt-12">
                <SectionHeader
                    title="Bestsellers"
                    subtitle="Loved by TSGo shoppers"
                    action={{ label: "See all", to: ROUTES.categories }}
                />
                {bestsellersQuery.isLoading ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <ProductCardSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
                        data-testid="home-bestsellers"
                    >
                        {bestsellers.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </section>
                </>
            )}

            {/* Trust strip */}
            <section className="tsgo-container mt-12">
                <div
                    data-testid="home-trust-strip"
                    className="grid grid-cols-1 gap-4 rounded-3xl border border-border/60 bg-muted/30 p-4 sm:grid-cols-3 sm:p-6"
                >
                    <TrustItem
                        icon={<Clock3 className="h-5 w-5" />}
                        title="Delivered in minutes"
                        body="Average delivery time of 12 minutes across serviced areas."
                    />
                    <TrustItem
                        icon={<ShieldCheck className="h-5 w-5" />}
                        title="Safe & secure"
                        body="Encrypted payments and hygiene-certified fulfilment centers."
                    />
                    <TrustItem
                        icon={<Truck className="h-5 w-5" />}
                        title="Free delivery"
                        body="On orders above ₹199. No hidden fees, ever."
                    />
                </div>
            </section>
        </div>
    );
}

function TrustItem({
    icon,
    title,
    body,
}: {
    icon: React.ReactNode;
    title: string;
    body: string;
}) {
    return (
        <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/10 text-brand">
                {icon}
            </span>
            <div>
                <p className="text-sm font-bold text-foreground">{title}</p>
                <p className="text-xs text-muted-foreground">{body}</p>
            </div>
        </div>
    );
}
